import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("If an account exists, you'll receive a reset email.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="premium-card rounded-2xl p-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66]/20 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
            <p className="text-[#A1A1AA] mb-6">
              If an account exists for <span className="text-white">{email}</span>, we've sent instructions to reset your password.
            </p>
            <p className="text-sm text-[#71717A] mb-6">
              The link will expire in 1 hour. Check your spam folder if you don't see the email.
            </p>
            <Link to="/login">
              <Button 
                className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Credit2Cash" 
            className="h-24 mx-auto logo-glow mb-4"
            data-testid="forgot-password-logo"
          />
          <h1 className="text-2xl font-bold text-white">Forgot Password?</h1>
          <p className="text-[#A1A1AA] mt-2">Enter your email to receive reset instructions</p>
        </div>

        {/* Form */}
        <div className="premium-card rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 input-dark"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFD700] text-black font-bold py-6 hover:bg-[#FFD700]/90 neon-glow-gold"
              data-testid="send-reset-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A1A1AA]">
              Remember your password?{" "}
              <Link 
                to="/login" 
                className="text-[#00FF66] hover:underline font-semibold"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
